// Copyright (c) 2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './ImageIssuesSection.scss';
import { useTranslation } from 'react-i18next';
import { storage } from '../../context/firebase';
import BaleCard from '../BaleCard/BaleCard';
import ImageSquare from '../../shared_components/ImageSquare/ImageSquare';
import alert_on_icon from '../../assets/icons/red/alert.png';
import { useAuthStore } from '../../stores/authStore';
import { getLabelDisplayName } from '../../shared_components/utils/FirebaseLabelsetsUtils';
import { Eye } from 'lucide-react';

export default function ImageIssuesSection({
  delivery,
  deliveryId,
  importedBalesData,
  showBale,
  showDeliveryImage,
  setAnalysisSummary,
}) {
  const { i18n, t } = useTranslation();
  const globalLabelsets = useAuthStore((state) => state.globalLabelsets);
  const [expanded, setExpanded] = React.useState(false);
  const [showAllProblemImages, setShowAllProblemImages] = React.useState(false);
  const [showAllProblemDeliveryPhotos, setShowAllProblemDeliveryPhotos] = React.useState(false);

  function getBaleIdsByImageQuality() {
    const result = {};
    if (!importedBalesData || typeof importedBalesData !== 'object') {
      return result;
    }
    Object.entries(importedBalesData).forEach(([baleId, baleData]) => {
      const issueLabel = baleData?.predicted_labels?.image_issues?.label;
      if (!issueLabel) {
        return;
      }
      const addIssue = (issue) => {
        if (typeof issue !== 'string') return;
        const key = issue.trim();
        if (!key) return;
        if (!result[key]) {
          result[key] = [];
        }
        if (!result[key].includes(baleId)) {
          result[key].push(baleId);
        }
      };
      if (Array.isArray(issueLabel)) {
        issueLabel.forEach(addIssue);
      } else {
        addIssue(issueLabel);
      }
    });
    return result;
  }

  function getBaleIdsWithLaminateLabel() {
    const result = [];
    Object.entries(importedBalesData).forEach(([baleId, baleData]) => {
      const baleSide = baleData?.predicted_labels?.bale_side?.label;
      if (baleSide === 'laminate') {
        result.push(baleId);
      }
    });
    return result;
  }

  function getBaleIdsWithAnomalyLabel() {
    const result = [];
    Object.entries(importedBalesData).forEach(([baleId, baleData]) => {
      const anomalyLabel = baleData?.predicted_labels?.anomaly_score?.label;
      if (anomalyLabel === 'anomaly') {
        result.push(baleId);
      }
    });
    return result;
  }

  function formatKeyToDisplay(key) {
    if (!key) return '';

    if (key in (globalLabelsets?.image_issues?.labels ?? {})) {
      return getLabelDisplayName(globalLabelsets?.image_issues, key, i18n.language);
    }

    if (key in (globalLabelsets?.bale_side?.labels || {})) {
      return getLabelDisplayName(globalLabelsets?.bale_side, key, i18n.language);
    }

    if (key === 'anomaly') {
      return getLabelDisplayName(globalLabelsets?.anomaly_score, 'anomaly', i18n.language);
    }

    return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  const baleIdsByImageQuality = getBaleIdsByImageQuality();
  baleIdsByImageQuality['laminate'] = getBaleIdsWithLaminateLabel();
  baleIdsByImageQuality['anomaly'] = getBaleIdsWithAnomalyLabel();
  const baleIdsByImageIssues = Object.fromEntries(
    Object.entries(baleIdsByImageQuality || {}).filter(([key]) => key !== 'no_issues'),
  );

  const baleIdsWithImageIssues = Array.from(
    new Set(Object.values(baleIdsByImageIssues).flatMap((ids) => (Array.isArray(ids) ? ids : []))),
  );
  const deliveryImageIdsWithIssues = Array.from(
    new Set(
      baleIdsWithImageIssues.map((baleId) => {
        return importedBalesData?.[baleId]?.delivery_image_id;
      }),
    ),
  );
  const totalBales = Object.keys(delivery?.bales || {}).length;
  const balesAnalyzed = totalBales - baleIdsWithImageIssues.length;
  const balesAnalyzedIssue =
    baleIdsWithImageIssues.length > 0
      ? `${t('issues')}: ${Object.keys(baleIdsByImageIssues)
          .map((key) => formatKeyToDisplay(key))
          .join(', ')}`
      : t('no_issues');

  React.useEffect(() => {
    setAnalysisSummary((prev) => ({
      ...prev,
      numBalesAnalyzed: balesAnalyzed,
    }));
  }, [balesAnalyzed]);

  return (
    <div className='delivery--ai-nested-card expandable'>
      <div className='delivery--ai-nested-card-header' onClick={() => setExpanded(!expanded)}>
        <div className='delivery--ai-nested-card-info'>
          <div className='delivery--ai-nested-card-icon-wrapper blue'>
            <Eye className='delivery--ai-nested-card-icon' />
          </div>
          <div className='delivery--ai-nested-card-text'>
            <h4 className='delivery--ai-nested-card-title'>{t('bales_analyzed')}</h4>
            <p className='delivery--ai-nested-card-subtitle'>{balesAnalyzedIssue}</p>
          </div>
        </div>
        <div className='delivery--ai-nested-card-value'>
          <div className='delivery--ai-nested-card-main-value'>
            <span>{balesAnalyzed}</span>
            <span className='light-text'>/{Object.keys(delivery?.bales || {}).length}</span>
          </div>
        </div>
      </div>
      {expanded && (
        <div className='delivery--ai-nested-card-expanded'>
          <div className='delivery--ai-expanded-content'>
            {baleIdsWithImageIssues.length > 0 ? (
              <>
                <h5 className='delivery--ai-breakdown-header'>{t('problem_delivery_photos')}:</h5>
                <div className='delivery--ai-error-images-grid'>
                  {(() => {
                    const INITIAL_DELIVERY_DISPLAY_COUNT = 6;
                    const displayedDeliveryImages = showAllProblemDeliveryPhotos
                      ? deliveryImageIdsWithIssues
                      : deliveryImageIdsWithIssues.slice(0, INITIAL_DELIVERY_DISPLAY_COUNT);
                    const hasMoreDeliveryImages = deliveryImageIdsWithIssues.length > INITIAL_DELIVERY_DISPLAY_COUNT;

                    return (
                      <>
                        {displayedDeliveryImages.map((deliveryImageId) => {
                          return (
                            <div
                              key={deliveryImageId}
                              className='delivery--ai-error-image-wrapper'
                              onClick={() => showDeliveryImage(deliveryImageId)}
                            >
                              <ImageSquare
                                storage={storage}
                                imageStoragePath={delivery.delivery_images?.[deliveryImageId]?.image_storage_path}
                                icons={delivery.delivery_images?.[deliveryImageId]?.flagged ? [alert_on_icon] : []}
                                hasImageQualityIssues={'blurry'}
                                size={128}
                              />
                            </div>
                          );
                        })}
                        {hasMoreDeliveryImages && (
                          <button
                            className='delivery--ai-problem-images-see-more'
                            onClick={() => setShowAllProblemDeliveryPhotos(!showAllProblemDeliveryPhotos)}
                          >
                            <div className='delivery--ai-see-more-content'>
                              <span className='delivery--ai-see-more-text'>
                                {showAllProblemDeliveryPhotos
                                  ? t('show_less')
                                  : `${t('see_more')} (${
                                      deliveryImageIdsWithIssues.length - INITIAL_DELIVERY_DISPLAY_COUNT
                                    })`}
                              </span>
                              <div className='delivery--ai-see-more-icon'>
                                {showAllProblemDeliveryPhotos ? '−' : '+'}
                              </div>
                            </div>
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>

                <h5 className='delivery--ai-breakdown-header' style={{ marginTop: '1.5rem' }}>
                  {t('problem_bales')}:
                </h5>
                <div className='delivery--ai-error-images-grid'>
                  {(() => {
                    const INITIAL_DISPLAY_COUNT = 6;
                    const displayedBales = showAllProblemImages
                      ? baleIdsWithImageIssues
                      : baleIdsWithImageIssues.slice(0, INITIAL_DISPLAY_COUNT);
                    const hasMoreImages = baleIdsWithImageIssues.length > INITIAL_DISPLAY_COUNT;

                    return (
                      <>
                        {displayedBales.map((baleId) => {
                          return (
                            <div
                              key={baleId}
                              className='delivery--ai-error-image-wrapper'
                              onClick={() => showBale(baleId)}
                            >
                              <BaleCard
                                imageStoragePath={delivery.bales?.[baleId]?.image_storage_path}
                                flagged={false}
                                baleId={baleId}
                                deliveryId={deliveryId}
                              />
                            </div>
                          );
                        })}
                        {hasMoreImages && (
                          <button
                            className='delivery--ai-problem-images-see-more'
                            onClick={() => setShowAllProblemImages(!showAllProblemImages)}
                          >
                            <div className='delivery--ai-see-more-content'>
                              <span className='delivery--ai-see-more-text'>
                                {showAllProblemImages
                                  ? t('show_less')
                                  : `${t('see_more')} (${baleIdsWithImageIssues.length - INITIAL_DISPLAY_COUNT})`}
                              </span>
                              <div className='delivery--ai-see-more-icon'>{showAllProblemImages ? '−' : '+'}</div>
                            </div>
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </>
            ) : (
              <div className='delivery--ai-error-images-grid'>
                <div className='delivery--ai-no-errors'>
                  <div className='delivery--ai-no-errors-icon'>✓</div>
                  <p>{t('no_image_errors_detected')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
